<template>
  <div class="mlb" :class="{'mlb--picker': picker}"
       @dragenter.prevent="onDragEnter" @dragover.prevent="onDragOver" @dragleave="onDragLeave" @drop.prevent="onDrop">

    <!-- drop overlay -->
    <div class="mlb-drop" v-if="dragging_files">
      <div>فایل‌ها را اینجا رها کنید
        <small>{{ current_folder ? 'آپلود در «' + current_folder.name + '»' : 'هر فایل خودکار به پوشهٔ نوع خودش می‌رود' }}</small>
      </div>
    </div>

    <!-- folders (RTL: right side) -->
    <div class="mlb-tree-backdrop" :class="{open: tree_open}" @click="tree_open = false"></div>
    <aside class="mlb-tree" :class="{open: tree_open}">
      <div class="mlb-tree__head">
        <span>پوشه‌ها</span>
        <button type="button" class="mlb-btn mlb-btn--ghost mlb-btn--xs" :disabled="!current_folder" @click="create_folder" title="پوشهٔ جدید داخل پوشهٔ انتخاب‌شده">+ پوشه</button>
      </div>
      <div class="mlb-tree__row" :class="{active: !folder_id}" @click="open_folder(null)"
           @dragover.prevent @drop.stop.prevent="drop_on_folder(null, $event)">
        <span class="mlb-tree__name">همهٔ رسانه‌ها</span>
      </div>
      <div v-for="f in tree_rows" :key="f.id"
           class="mlb-tree__row" :class="{active: folder_id === f.id, root: f.is_root}"
           :style="{paddingRight: (10 + f.depth * 14) + 'px'}"
           :draggable="!f.is_root"
           @dragstart="drag_folder_start(f, $event)"
           @dragover.prevent @drop.stop.prevent="drop_on_folder(f, $event)"
           @click="open_folder(f)">
        <span class="mlb-tree__toggle" v-if="f.has_children" @click.stop="toggle(f)">{{ collapsed[f.id] ? '‹' : '⌄' }}</span>
        <span class="mlb-tree__toggle" v-else></span>
        <span class="mlb-tree__name">{{ f.name }}</span>
        <span class="mlb-tree__count">{{ f.count }}</span>
        <span class="mlb-tree__actions" v-if="!f.is_root">
          <button type="button" title="تغییر نام" @click.stop="rename_folder(f)">✎</button>
          <button type="button" title="حذف پوشهٔ خالی" @click.stop="delete_folder(f)">✕</button>
        </span>
      </div>
      <p class="mlb-hint">برای جابجایی، فایل‌ها یا پوشه‌ها را روی پوشهٔ مقصد بکشید.</p>
    </aside>

    <!-- main -->
    <section class="mlb-main">
      <div class="mlb-toolbar">
        <button type="button" class="mlb-btn mlb-btn--ghost mlb-tree-toggle" @click="tree_open = !tree_open">پوشه‌ها</button>
        <select v-model="type" class="mlb-input mlb-input--select" @change="reload">
          <option v-for="t in type_options" :key="t.value" :value="t.value">{{ t.label }}</option>
        </select>
        <input class="mlb-input mlb-input--search" v-model="search" @input="search_debounced" placeholder="جستجو در نام، عنوان، alt"/>
        <span class="mlb-spacer"></span>
        <button type="button" class="mlb-btn" @click="$refs.file_input.click()">آپلود فایل</button>
        <input ref="file_input" type="file" multiple class="d-none" :accept="accept" @change="on_pick_files"/>
      </div>

      <div class="mlb-selbar" v-if="selected.length">
        <span>{{ selected.length }} مورد انتخاب شده</span>
        <select class="mlb-input mlb-input--select" v-model="move_target">
          <option :value="null">انتقال به...</option>
          <option v-for="f in tree_rows" :key="f.id" :value="f.id">{{ '— '.repeat(f.depth) + f.name }}</option>
        </select>
        <button type="button" class="mlb-btn mlb-btn--ghost" :disabled="!move_target" @click="move_selected(move_target)">انتقال</button>
        <button type="button" class="mlb-btn mlb-btn--danger" @click="delete_items(selected)">حذف</button>
        <button type="button" class="mlb-btn mlb-btn--ghost" @click="clear_selection">لغو انتخاب</button>
      </div>

      <div class="mlb-uploads" v-if="uploads.length">
        <div v-for="u in uploads" :key="u.key" class="mlb-upload" :class="'is-' + u.state">
          <span class="mlb-upload__name" dir="auto">{{ u.name }}</span>
          <span class="mlb-upload__bar"><i :style="{width: u.progress + '%'}"></i></span>
          <span class="mlb-upload__state">{{ u.state === 'error' ? u.msg : (u.state === 'done' ? (u.duplicate ? 'تکراری (موجود)' : 'انجام شد') : u.progress + '%') }}</span>
        </div>
        <button type="button" class="mlb-btn mlb-btn--ghost mlb-btn--xs" v-if="!uploading" @click="uploads = []">بستن</button>
      </div>

      <div class="mlb-grid" ref="grid">
        <div v-for="item in items" :key="item.id"
             class="mlb-card" :class="{selected: is_selected(item.id), active: detail && detail.id === item.id}"
             draggable="true" @dragstart="drag_items_start(item, $event)"
             @click="click_item(item, $event)" @dblclick="dbl_item(item)">
          <div class="mlb-thumb">
            <img v-if="item.type === 'image' || item.type === 'icon'" :src="item.url" :alt="item.alt" loading="lazy"/>
            <video v-else-if="item.type === 'video'" :src="item.url + '#t=0.5'" preload="metadata" muted></video>
            <div v-else-if="item.type === 'audio'" class="mlb-ph">♪<small>{{ fmt_duration(item.duration) || 'صوت' }}</small></div>
            <div v-else class="mlb-ph">{{ (item.file_name.split('.').pop() || '').toUpperCase() }}</div>
            <span class="mlb-badge" v-if="item.type === 'video'">▶ {{ fmt_duration(item.duration) }}</span>
            <span class="mlb-check" v-if="is_selected(item.id)">✓</span>
          </div>
          <div class="mlb-card__name" dir="auto" :title="item.name">{{ item.title || item.name }}</div>
        </div>
        <template v-if="loading">
          <div v-for="n in (items.length ? 5 : 15)" :key="'sk' + n" class="mlb-card mlb-sk" aria-hidden="true"><div class="mlb-thumb mlb-shimmer"></div><div class="mlb-card__name"><span class="mlb-shimmer" style="display:block;height:10px;border-radius:4px"></span></div></div>
        </template>
        <div ref="sentinel" class="mlb-sentinel" aria-hidden="true"></div>
        <div v-if="!items.length && !loading" class="mlb-empty">موردی یافت نشد. فایل‌ها را اینجا بکشید یا «آپلود فایل» را بزنید.</div>
      </div>
      <div class="mlb-more">
        <span class="text-muted">{{ items.length }} از {{ total }}</span>
        <button type="button" class="mlb-btn mlb-btn--ghost" v-if="load_error" @click="load_more_retry">خطا در دریافت؛ تلاش دوباره</button>
        <span v-else-if="items.length && items.length >= total" class="text-muted">همه موارد نمایش داده شد</span>
        <span v-if="loading" class="text-muted">در حال بارگذاری...</span>
      </div>
    </section>

    <!-- details (RTL: left side) -->
    <aside class="mlb-detail" v-if="detail && picker">
      <div class="mlb-detail__preview">
        <img v-if="detail.type === 'image' || detail.type === 'icon'" :src="detail.url" :alt="detail.alt"/>
        <video v-else-if="detail.type === 'video'" :src="detail.url" controls preload="metadata"></video>
        <audio v-else-if="detail.type === 'audio'" :src="detail.url" controls preload="metadata"></audio>
        <a v-else :href="detail.url" target="_blank" rel="noopener">مشاهدهٔ فایل</a>
      </div>
      <dl class="mlb-meta">
        <dt>نوع</dt><dd dir="ltr">{{ detail.mime }}</dd>
        <dt>حجم</dt><dd dir="ltr">{{ fmt_size(detail.size) }}</dd>
        <template v-if="detail.width"><dt>ابعاد</dt><dd dir="ltr">{{ detail.width }}×{{ detail.height }}</dd></template>
        <template v-if="detail.duration"><dt>مدت</dt><dd dir="ltr">{{ fmt_duration(detail.duration) }}</dd></template>
        <dt>تاریخ</dt><dd dir="ltr">{{ String(detail.created_at || '').slice(0, 10) }}</dd>
      </dl>
      <label class="mlb-label">نام</label>
      <input class="mlb-input" v-model="edit.name" dir="auto"/>
      <label class="mlb-label">عنوان</label>
      <input class="mlb-input" v-model="edit.title" dir="auto"/>
      <label class="mlb-label" v-if="detail.type === 'image' || detail.type === 'icon'">متن جایگزین (alt)</label>
      <input class="mlb-input" v-if="detail.type === 'image' || detail.type === 'icon'" v-model="edit.alt" dir="auto"/>
      <label class="mlb-label">آدرس فایل</label>
      <div class="mlb-url">
        <input class="mlb-input" :value="detail.url" readonly dir="ltr" @focus="$event.target.select()"/>
        <button type="button" class="mlb-btn mlb-btn--ghost" @click="copy_url(detail.url)">کپی</button>
      </div>
      <div class="mlb-detail__actions">
        <button type="button" class="mlb-btn" :disabled="saving" @click="save_detail">ذخیره</button>
        <button type="button" class="mlb-btn mlb-btn--danger" @click="delete_items([detail.id])">حذف</button>
      </div>
    </aside>

    <!-- WordPress-like attachment details lightbox (library page; the picker keeps its side panel) -->
    <div v-if="lightbox && detail" class="mlb-lb" role="dialog" aria-modal="true" aria-label="جزئیات پیوست" @click.self="lb_close">
      <div class="mlb-lb__box">
        <header class="mlb-lb__head">
          <strong>جزئیات پیوست</strong>
          <span class="mlb-lb__pos" v-if="lb_index >= 0">{{ lb_index + 1 }} / {{ items.length }}</span>
          <span class="mlb-spacer"></span>
          <button type="button" class="mlb-lb__icon" :title="'قبلی (→)'" aria-label="قبلی" :disabled="lb_index <= 0" @click="lb_nav(-1)"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M9 6l6 6-6 6"/></svg></button>
          <button type="button" class="mlb-lb__icon" :title="'بعدی (←)'" aria-label="بعدی" :disabled="lb_index < 0 || lb_index >= items.length - 1" @click="lb_nav(1)"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M15 6l-6 6 6 6"/></svg></button>
          <button type="button" class="mlb-lb__icon" :title="'بستن (Esc)'" aria-label="بستن" @click="lb_close"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg></button>
        </header>
        <div class="mlb-lb__body">
          <div class="mlb-lb__media">
            <img v-if="detail.type === 'image' || detail.type === 'icon'" :src="detail.url" :alt="detail.alt"/>
            <video v-else-if="detail.type === 'video'" :src="detail.url" controls preload="metadata"></video>
            <audio v-else-if="detail.type === 'audio'" :src="detail.url" controls preload="metadata"></audio>
            <a v-else :href="detail.url" target="_blank" rel="noopener" class="mlb-btn">مشاهدهٔ فایل</a>
          </div>
          <aside class="mlb-lb__side">
            <dl class="mlb-meta">
              <dt>نام فایل</dt><dd dir="ltr">{{ detail.file_name }}</dd>
              <dt>نوع</dt><dd dir="ltr">{{ detail.mime }}</dd>
              <dt>حجم</dt><dd dir="ltr">{{ fmt_size(detail.size) }}</dd>
              <template v-if="detail.width"><dt>ابعاد</dt><dd dir="ltr">{{ detail.width }} × {{ detail.height }} px</dd></template>
              <template v-if="detail.duration"><dt>مدت</dt><dd dir="ltr">{{ fmt_duration(detail.duration) }}</dd></template>
              <dt>تاریخ بارگذاری</dt><dd>{{ fmt_date(detail.created_at) }}</dd>
            </dl>
            <label class="mlb-label">نام</label>
            <input class="mlb-input" v-model="edit.name" dir="auto"/>
            <label class="mlb-label">عنوان</label>
            <input class="mlb-input" v-model="edit.title" dir="auto"/>
            <template v-if="detail.type === 'image' || detail.type === 'icon'">
              <label class="mlb-label">متن جایگزین (alt)</label>
              <input class="mlb-input" v-model="edit.alt" dir="auto"/>
              <small class="mlb-hint">برای سئو و دسترس‌پذیری، تصویر را کوتاه توصیف کنید.</small>
            </template>
            <label class="mlb-label">آدرس فایل</label>
            <div class="mlb-url">
              <input class="mlb-input" :value="detail.url" readonly dir="ltr" @focus="$event.target.select()"/>
              <button type="button" class="mlb-btn mlb-btn--ghost" @click="copy_url(detail.url)">کپی</button>
            </div>
            <div class="mlb-detail__actions">
              <button type="button" class="mlb-btn" :disabled="saving" @click="save_detail">{{ saving ? 'در حال ذخیره...' : 'ذخیره' }}</button>
              <a :href="detail.url" target="_blank" rel="noopener" class="mlb-btn mlb-btn--ghost">نمایش فایل</a>
              <button type="button" class="mlb-btn mlb-btn--danger" @click="delete_items([detail.id])">حذف دائمی</button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/api/http";
const TYPE_OPTIONS = [
  {value: 'all', label: 'همهٔ انواع'},
  {value: 'image', label: 'تصاویر'},
  {value: 'video', label: 'ویدیوها'},
  {value: 'audio', label: 'صوت‌ها'},
  {value: 'icon', label: 'آیکون‌ها'},
  {value: 'document', label: 'اسناد'},
];
const ACCEPT = {
  image: 'image/jpeg,image/png,image/webp,image/gif,image/avif',
  icon: 'image/svg+xml,image/x-icon,image/vnd.microsoft.icon,image/png,image/webp,.ico,.svg',
  video: 'video/mp4,video/webm',
  audio: 'audio/mpeg,audio/ogg,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,.m4a',
  document: 'application/pdf',
};

function guessType(file) {
  const t = (file.type || '').toLowerCase();
  const n = (file.name || '').toLowerCase();
  if (t === 'image/svg+xml' || t.indexOf('icon') >= 0 || /\.(svg|ico)$/.test(n)) return 'icon';
  if (t.indexOf('image/') === 0) return 'image';
  if (t.indexOf('video/') === 0) return 'video';
  if (t.indexOf('audio/') === 0) return 'audio';
  if (t === 'application/pdf') return 'document';
  return '';
}

import { imageScopeFor } from '@/utils/imageScope';
export default {
  name: 'MediaLibraryBrowser',
  props: {
    imageScope: String,
    picker: {type: Boolean, default: false},
    multiple: {type: Boolean, default: false},
    initialType: {type: String, default: 'all'},
    value: {type: Array, default: () => []}, // selected media ids (picker)
  },
  data() {
    return {
      type_options: TYPE_OPTIONS,
      type: this.initialType,
      folders: [],
      collapsed: {},
      folder_id: null,
      search: '',
      items: [],
      total: 0,
      page: 1,
      per_page: 20,
      load_error: false,
      io: null,
      tree_open: false,
      loading: false,
      selected: (this.value || []).slice(),
      known: {}, // id -> item (selected items that may not be in the current page)
      detail: null,
      edit: {name: '', title: '', alt: ''},
      saving: false,
      move_target: null,
      uploads: [],
      uploading: false,
      dragging_files: false,
      drag_depth: 0,
      search_timer: null,
      last_clicked: null,
      lightbox: false,
    };
  },
  computed: {
    lb_index() {
      return this.detail ? this.items.findIndex((x) => x.id === this.detail.id) : -1;
    },
    current_folder() {
      return this.folders.find((f) => f.id === this.folder_id) || null;
    },
    accept() {
      if (this.type !== 'all' && ACCEPT[this.type]) return ACCEPT[this.type];
      return Object.values(ACCEPT).join(',');
    },
    tree_rows() {
      const byParent = {};
      this.folders.forEach((f) => { (byParent[f.parent_id || 0] = byParent[f.parent_id || 0] || []).push(f); });
      const out = [];
      const walk = (pid, depth) => {
        (byParent[pid] || []).forEach((f) => {
          const kids = byParent[f.id] || [];
          out.push(Object.assign({}, f, {depth, has_children: kids.length > 0}));
          if (!this.collapsed[f.id]) walk(f.id, depth + 1);
        });
      };
      walk(0, 0);
      return out;
    },
  },
  watch: {
    value(v) {
      if (JSON.stringify(v || []) !== JSON.stringify(this.selected)) this.selected = (v || []).slice();
    },
  },
  mounted() {
    document.addEventListener('keydown', this.on_lb_key);
    this.load_folders();
    this.reload();
  },
  beforeDestroy() {
    if (this.io) this.io.disconnect();
    document.removeEventListener('keydown', this.on_lb_key);
    clearTimeout(this.search_timer);
  },
  methods: {
    toast(message, type) {
      if (this.$toast) this.$toast.open({message: message, type: type || 'success'});
    },
    api(url, data, extra) {
      const fd = new FormData();
      Object.keys(data || {}).forEach((k) => {
        const v = data[k];
        if (Array.isArray(v)) v.forEach((x) => fd.append(k + '[]', x));
        else if (v !== undefined && v !== null) fd.append(k, v);
      });
      return axios.post('/api/admin/media/' + url, fd, Object.assign({show_pros: false}, extra || {})).then((r) => r.data);
    },
    load_folders() {
      return this.api('folders').then((d) => { if (d && d.folders) this.folders = d.folders; });
    },
    reload() {
      this.page = 1;
      this.items = [];
      return this.fetch_page();
    },
    load_more() {
      if (this.loading || this.load_error || this.items.length >= this.total) return;
      this.page++;
      this.fetch_page();
    },
    load_more_retry() {
      this.load_error = false;
      this.fetch_page();
    },
    setup_io() {
      if (this.io || typeof IntersectionObserver === 'undefined' || !this.$refs.sentinel) return;
      const root = this.$el.querySelector('.mlb-grid');
      this.io = new IntersectionObserver((e) => { if (e[0].isIntersecting) this.load_more(); }, { root, rootMargin: '300px' });
      this.io.observe(this.$refs.sentinel);
    },
    fetch_page() {
      this.loading = true;
      this.load_error = false;
      const req = {type: this.type, page: this.page, per_page: this.per_page, search: this.search};
      if (this.folder_id) req.folder_id = this.folder_id;
      return this.api('list', req).then((d) => {
        if (!d || d.error) throw new Error((d && d.msg) || 'خطا');
        const seen = {};
        this.items.forEach((x) => { seen[x.id] = 1; });
        d.items.forEach((x) => { if (!seen[x.id]) this.items.push(x); });
        this.total = d.total;
      }).catch((e) => { this.load_error = true; if (this.page > 1) this.page--; this.toast(e.message || 'خطا در دریافت رسانه‌ها', 'error'); })
        .finally(() => { this.loading = false; this.$nextTick(() => this.setup_io()); });
    },
    search_debounced() {
      clearTimeout(this.search_timer);
      this.search_timer = setTimeout(() => this.reload(), 350);
    },
    open_folder(f) {
      this.tree_open = false;
      this.folder_id = f ? f.id : null;
      if (f && this.type !== 'all' && this.type !== f.root_type) this.type = 'all';
      this.reload();
    },
    toggle(f) {
      this.$set(this.collapsed, f.id, !this.collapsed[f.id]);
    },
    // ---------- folders ----------
    create_folder() {
      if (!this.current_folder) return;
      const name = window.prompt('نام پوشهٔ جدید داخل «' + this.current_folder.name + '»:');
      if (!name || !name.trim()) return;
      this.api('folder_create', {parent_id: this.current_folder.id, name: name.trim()}).then((d) => {
        this.toast(d.msg, d.error ? 'error' : 'success');
        this.load_folders();
      });
    },
    rename_folder(f) {
      const name = window.prompt('نام جدید پوشه:', f.name);
      if (!name || !name.trim() || name.trim() === f.name) return;
      this.api('folder_rename', {id: f.id, name: name.trim()}).then((d) => {
        this.toast(d.msg, d.error ? 'error' : 'success');
        this.load_folders();
      });
    },
    delete_folder(f) {
      if (!window.confirm('پوشهٔ «' + f.name + '» حذف شود؟ (فقط پوشهٔ خالی)')) return;
      this.api('folder_delete', {id: f.id}).then((d) => {
        this.toast(d.msg, d.error ? 'error' : 'success');
        if (!d.error && this.folder_id === f.id) this.open_folder(null);
        this.load_folders();
      });
    },
    drag_folder_start(f, ev) {
      ev.dataTransfer.setData('text/x-media-folder', String(f.id));
      ev.dataTransfer.effectAllowed = 'move';
    },
    drag_items_start(item, ev) {
      const ids = this.is_selected(item.id) ? this.selected.slice() : [item.id];
      ev.dataTransfer.setData('text/x-media-items', ids.join(','));
      ev.dataTransfer.effectAllowed = 'move';
    },
    drop_on_folder(f, ev) {
      this.dragging_files = false;
      this.drag_depth = 0;
      const dt = ev.dataTransfer;
      if (dt.files && dt.files.length) {
        if (f && this.folder_id !== f.id) this.open_folder(f);
        this.upload_files(Array.from(dt.files));
        return;
      }
      if (!f) return;
      const folderId = dt.getData('text/x-media-folder');
      if (folderId) {
        if (Number(folderId) === f.id) return;
        this.api('folder_move', {id: folderId, parent_id: f.id}).then((d) => {
          this.toast(d.msg, d.error ? 'error' : 'success');
          this.load_folders();
        });
        return;
      }
      const ids = (dt.getData('text/x-media-items') || '').split(',').filter(Boolean);
      if (ids.length) this.move_ids(ids, f.id);
    },
    // ---------- selection ----------
    is_selected(id) {
      return this.selected.indexOf(id) >= 0;
    },
    emit_selection() {
      this.$emit('input', this.selected.slice());
      this.$emit('items', this.selected.map((id) => this.known[id] || this.items.find((x) => x.id === id)).filter(Boolean));
    },
    click_item(item, ev) {
      // library page: plain click opens the attachment lightbox (ctrl/cmd/shift still select)
      if (!this.picker && !ev.shiftKey && !ev.ctrlKey && !ev.metaKey) {
        this.$set(this.known, item.id, item);
        this.open_detail(item);
        this.lightbox = true;
        return;
      }
      this.$set(this.known, item.id, item);
      this.open_detail(item);
      const multi = this.picker ? this.multiple : true;
      if (!multi) {
        this.selected = [item.id];
      } else if (ev.shiftKey && this.last_clicked !== null) {
        const a = this.items.findIndex((x) => x.id === this.last_clicked);
        const b = this.items.findIndex((x) => x.id === item.id);
        this.items.slice(Math.min(a, b), Math.max(a, b) + 1).forEach((x) => {
          this.$set(this.known, x.id, x);
          if (!this.is_selected(x.id)) this.selected.push(x.id);
        });
      } else if (this.picker || ev.ctrlKey || ev.metaKey) {
        const i = this.selected.indexOf(item.id);
        if (i >= 0) this.selected.splice(i, 1); else this.selected.push(item.id);
      } else {
        this.selected = this.is_selected(item.id) && this.selected.length === 1 ? [] : [item.id];
      }
      this.last_clicked = item.id;
      this.emit_selection();
    },
    dbl_item(item) {
      if (!this.picker) return;
      if (!this.is_selected(item.id)) { this.selected = this.multiple ? this.selected.concat([item.id]) : [item.id]; }
      this.$set(this.known, item.id, item);
      this.emit_selection();
      this.$emit('pick');
    },
    clear_selection() {
      this.selected = [];
      this.emit_selection();
    },
    lb_nav(d) {
      const i = this.lb_index + d;
      if (i < 0 || i >= this.items.length) return;
      this.open_detail(this.items[i]);
    },
    lb_close() {
      this.lightbox = false;
    },
    on_lb_key(e) {
      if (!this.lightbox || !this.detail) return;
      const tag = (e.target && e.target.tagName) || '';
      if (e.key === 'Escape') { this.lb_close(); return; }
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      // RTL: left arrow = next, right arrow = previous
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.lb_nav(1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.lb_nav(-1); }
    },
    fmt_date(v) {
      if (!v) return '';
      const d = new Date(v);
      if (isNaN(d.getTime())) return String(v).slice(0, 10);
      try { return d.toLocaleDateString('fa-IR', {year: 'numeric', month: 'long', day: 'numeric'}); } catch (e) { return String(v).slice(0, 10); }
    },
    open_detail(item) {
      this.detail = item;
      this.edit = {name: item.name || '', title: item.title || '', alt: item.alt || ''};
    },
    // ---------- edit / move / delete ----------
    save_detail() {
      if (!this.detail) return;
      this.saving = true;
      this.api('update', {id: this.detail.id, name: this.edit.name, title: this.edit.title, alt: this.edit.alt}).then((d) => {
        this.toast(d.msg, d.error ? 'error' : 'success');
        if (!d.error && d.item) {
          const i = this.items.findIndex((x) => x.id === d.item.id);
          if (i >= 0) this.items.splice(i, 1, d.item);
          this.detail = d.item;
          this.$set(this.known, d.item.id, d.item);
        }
      }).finally(() => { this.saving = false; });
    },
    move_selected(folderId) {
      this.move_ids(this.selected.slice(), folderId);
    },
    move_ids(ids, folderId) {
      this.api('move', {ids: ids, folder_id: folderId}).then((d) => {
        this.toast(d.msg, d.error ? 'error' : (d.skipped && d.skipped.length ? 'warning' : 'success'));
        this.move_target = null;
        this.load_folders();
        if (this.folder_id) this.reload();
      });
    },
    delete_items(ids, force) {
      ids = ids.slice();
      if (!force && !window.confirm(ids.length + ' مورد برای همیشه حذف شود؟')) return;
      this.api(ids.length === 1 ? 'delete' : 'bulk_delete', ids.length === 1 ? {id: ids[0], force: force ? 1 : 0} : {ids: ids, force: force ? 1 : 0}).then((d) => {
        if (d.error && d.in_use) {
          const lines = d.in_use.map((u) => {
            const parts = [];
            if (u.products.length) parts.push('محصول: ' + u.products.map((p) => p.name).slice(0, 5).join('، '));
            if (u.categories) parts.push(u.categories + ' دسته‌بندی');
            if (u.brands) parts.push(u.brands + ' برند');
            return '• #' + u.id + ' ← ' + parts.join(' / ');
          });
          if (window.confirm('هشدار: این فایل‌ها در حال استفاده‌اند و با حذف از آن‌ها برداشته می‌شوند:\n' + lines.join('\n') + '\n\nبا این حال حذف شود؟')) {
            this.delete_items(ids, true);
          }
          return;
        }
        this.toast(d.msg, d.error ? 'error' : 'success');
        if (!d.error) {
          this.items = this.items.filter((x) => ids.indexOf(x.id) < 0);
          this.total = Math.max(0, this.total - (d.deleted || 0));
          this.selected = this.selected.filter((x) => ids.indexOf(x) < 0);
          if (this.detail && ids.indexOf(this.detail.id) >= 0) this.detail = null;
          this.emit_selection();
          this.load_folders();
        }
      });
    },
    copy_url(url) {
      const done = () => this.toast('آدرس کپی شد');
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url).then(done);
      } else {
        const t = document.createElement('textarea');
        t.value = url; document.body.appendChild(t); t.select();
        try { document.execCommand('copy'); done(); } catch (e) { /* ignore */ }
        document.body.removeChild(t);
      }
    },
    // ---------- upload ----------
    on_pick_files(ev) {
      this.upload_files(Array.from(ev.target.files || []));
      ev.target.value = '';
    },
    onDragEnter(ev) {
      if (!ev.dataTransfer || Array.from(ev.dataTransfer.types || []).indexOf('Files') < 0) return;
      this.drag_depth++;
      this.dragging_files = true;
    },
    onDragOver() {},
    onDragLeave() {
      if (!this.dragging_files) return;
      this.drag_depth = Math.max(0, this.drag_depth - 1);
      if (this.drag_depth === 0) this.dragging_files = false;
    },
    onDrop(ev) {
      this.dragging_files = false;
      this.drag_depth = 0;
      const files = Array.from((ev.dataTransfer && ev.dataTransfer.files) || []);
      if (files.length) this.upload_files(files);
    },
    upload_files(files) {
      if (!files.length) return;
      const folder = this.current_folder;
      const jobs = files.map((file, i) => {
        const u = {key: Date.now() + '-' + i + '-' + file.name, name: file.name, progress: 0, state: 'waiting', msg: '', duplicate: false};
        this.uploads.push(u);
        return {file, u};
      });
      this.uploading = true;
      let next = 0;
      const worker = () => {
        if (next >= jobs.length) return Promise.resolve();
        const job = jobs[next++];
        return this.upload_one(job.file, job.u, folder).then(worker);
      };
      Promise.all([worker(), worker()]).then(() => {
        this.uploading = false;
        this.load_folders();
        const ok = jobs.filter((j) => j.u.state === 'done').length;
        this.toast(ok + ' از ' + jobs.length + ' فایل آپلود شد', ok === jobs.length ? 'success' : 'warning');
      });
    },
    upload_one(file, u, folder) {
      u.state = 'working';
      const fd = new FormData();
      fd.append('files', file, file.name);
      const sc = imageScopeFor(this.$route, this.imageScope);
      if (sc) fd.append('image_scope', sc);
      // keep the chosen folder only if it suits the file; otherwise the server routes it to its type's root
      if (folder && guessType(file) && (guessType(file) === folder.root_type || (folder.root_type === 'icon' && /png|webp/i.test(file.type)))) {
        fd.append('folder_id', folder.id);
      }
      return axios.post('/api/admin/media/upload', fd, {
        show_pros: false,
        timeout: 0,
        onUploadProgress: (e) => { if (e.total) u.progress = Math.min(99, Math.round((e.loaded * 100) / e.total)); },
      }).then((r) => {
        const d = r.data || {};
        if (d.error || !d.items || !d.items.length) throw new Error(d.msg || 'خطا');
        const item = d.items[0];
        u.progress = 100;
        u.state = 'done';
        u.duplicate = !!item.duplicate;
        if (!this.items.some((x) => x.id === item.id)) { this.items.unshift(item); this.total++; }
        this.$set(this.known, item.id, item);
        if (this.picker) {
          if (!this.multiple) this.selected = [item.id];
          else if (!this.is_selected(item.id)) this.selected.push(item.id);
          this.open_detail(item);
          this.emit_selection();
        }
      }).catch((e) => {
        u.state = 'error';
        const d = e && e.response && e.response.data;
        u.msg = (d && d.msg) || (e && e.response && e.response.status === 413 ? 'حجم فایل بیش از حد مجاز سرور است' : (e && e.message) || 'خطا');
      });
    },
    // ---------- format ----------
    fmt_size(n) {
      n = Number(n) || 0;
      if (n < 1024) return n + ' B';
      if (n < 1048576) return (n / 1024).toFixed(1) + ' KB';
      return (n / 1048576).toFixed(1) + ' MB';
    },
    fmt_duration(s) {
      s = Math.round(Number(s) || 0);
      if (!s) return '';
      const m = Math.floor(s / 60);
      const r = s % 60;
      return m + ':' + (r < 10 ? '0' : '') + r;
    },
  },
};
</script>

<style scoped>
.mlb { position: relative; display: grid; grid-template-columns: 230px minmax(0, 1fr) auto; gap: 16px; min-height: calc(100vh - 210px); direction: rtl; color: var(--text); }
.mlb-tree { border-left: 1px solid var(--border); padding-left: 12px; overflow: auto; max-height: calc(100vh - 210px); }
.mlb--picker {
  min-height: 0 !important;
  height: 100%;
  max-height: 100%;
  flex: 1 1 auto;
}
.mlb--picker .mlb-tree,
.mlb--picker .mlb-detail {
  max-height: none !important;
  height: 100%;
  overflow: auto;
}
.mlb--picker .mlb-main {
  min-height: 0;
  height: 100%;
  overflow: hidden;
}
.mlb--picker .mlb-grid {
  max-height: none !important;
  min-height: 0 !important;
  flex: 1 1 auto;
  overflow: auto;
}
.mlb-tree__head { display: flex; align-items: center; justify-content: space-between; font-weight: 600; margin-bottom: 8px; }
.mlb-tree__row { display: flex; align-items: center; gap: 4px; padding: 6px 10px; border-radius: 6px; cursor: pointer; user-select: none; font-size: 13px; }
.mlb-tree__row:hover { box-shadow: inset 0 0 0 1px var(--border); }
.mlb-tree__row.active { background: var(--accent-soft); color: var(--accent-text); font-weight: 600; }
.mlb-tree__row.root .mlb-tree__name { font-weight: 600; }
.mlb-tree__toggle { width: 14px; text-align: center; color: inherit; opacity: .6; }
.mlb-tree__name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mlb-tree__count { font-size: 11px; opacity: .6; }
.mlb-tree__actions { display: none; gap: 2px; }
.mlb-tree__row:hover .mlb-tree__actions { display: inline-flex; }
.mlb-tree__actions button { border: 0; background: transparent; color: inherit; padding: 0 3px; cursor: pointer; }
.mlb-hint { font-size: 11px; color: var(--muted); margin-top: 12px; }
.mlb-main { min-width: 0; display: flex; flex-direction: column; }
.mlb-toolbar, .mlb-selbar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 10px; }
.mlb-selbar { padding: 8px 10px; border: 1px solid var(--accent-ring); background: var(--accent-soft); border-radius: 8px; font-size: 13px; }
.mlb-spacer { flex: 1; }
.mlb-input { border: 1px solid var(--border); border-radius: 6px; padding: 6px 10px; font-size: 13px; background: var(--surface); color: var(--text); width: 100%; outline: none; }
.mlb-input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(37,99,235,.15); }
.mlb-input--select { width: auto; }
.mlb-input--search { width: 240px; max-width: 100%; }
.mlb-btn { border: 1px solid var(--accent); background: var(--accent); color: #fff; border-radius: 6px; padding: 6px 12px; font-size: 13px; cursor: pointer; white-space: nowrap; }
.mlb-btn:not(:disabled):hover { background: var(--accent-hover); border-color: var(--accent-hover); }
.mlb-btn:disabled { opacity: .4; cursor: default; }
.mlb-btn--ghost { background: var(--surface); color: var(--text); border-color: var(--border-strong); }
.mlb-btn--ghost:not(:disabled):hover { background: var(--surface); color: var(--text); border-color: var(--text); }
.mlb-btn--danger:not(:disabled):hover { background: #dc2626; color: #fff; border-color: #dc2626; }
.mlb-btn--danger { background: var(--surface); color: #dc2626; border-color: #dc2626; }
.mlb-btn--xs { padding: 2px 8px; font-size: 12px; }
.mlb-uploads { border: 1px solid var(--border); border-radius: 8px; padding: 8px 10px; margin-bottom: 10px; display: grid; gap: 6px; }
.mlb-upload { display: grid; grid-template-columns: minmax(0, 1fr) 140px auto; align-items: center; gap: 10px; font-size: 12px; }
.mlb-upload__name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mlb-upload__bar { height: 4px; background: var(--surface-2); border-radius: 4px; overflow: hidden; }
.mlb-upload__bar i { display: block; height: 100%; background: var(--accent); transition: width .2s; }
.mlb-upload.is-error { color: #dc2626; }
.mlb-upload.is-error .mlb-upload__bar i { background: #dc2626; }
.mlb-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); grid-auto-rows: max-content; gap: 10px; overflow: auto; max-height: calc(100vh - 290px); min-height: 300px; align-content: start; align-items: start; padding: 2px; }
.mlb-card { border: 1px solid var(--border); border-radius: 8px; overflow: hidden; cursor: pointer; background: var(--surface); display: flex; flex-direction: column; }
.mlb-card:hover { border-color: var(--muted); }
.mlb-card.selected { border-color: var(--accent); box-shadow: 0 0 0 2px var(--accent); }
/* always-square tile (padding hack works even where aspect-ratio is overridden/unsupported) */
.mlb-thumb { position: relative; width: 100%; height: 0; padding-top: 100%; aspect-ratio: auto; background: #f4f5f7; overflow: hidden; flex: 0 0 auto; }
.mlb-thumb > * { position: absolute; inset: 0; }
.mlb-thumb > .mlb-ph { display: flex; justify-content: center; }
.mlb-thumb > .mlb-badge { inset: auto auto 6px 6px; }
.mlb-thumb > .mlb-check { inset: 6px 6px auto auto; }
.mlb-thumb img, .mlb-thumb video { width: 100% !important; height: 100% !important; max-width: none !important; object-fit: cover; object-position: center; display: block; padding: 0; }
.mlb-ph { display: flex; flex-direction: column; align-items: center; font-size: 28px; font-weight: 700; color: var(--text); }
.mlb-ph small { font-size: 11px; font-weight: 400; color: var(--muted); }
.mlb-badge { position: absolute; bottom: 6px; left: 6px; background: #111; color: #fff; font-size: 10px; padding: 1px 6px; border-radius: 4px; direction: ltr; }
.mlb-check { position: absolute; top: 6px; right: 6px; width: 20px; height: 20px; border-radius: 50%; background: var(--accent); color: #fff; font-size: 12px; display: flex; align-items: center; justify-content: center; }
.mlb-card__name { font-size: 12px; padding: 6px 8px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; border-top: 1px solid var(--border); }
.mlb-empty { grid-column: 1 / -1; text-align: center; color: var(--muted); padding: 60px 10px; border: 1px dashed var(--border-strong); border-radius: 8px; }
.mlb-more { display: flex; align-items: center; justify-content: center; gap: 12px; padding: 10px 0; font-size: 12px; }
.mlb-detail { width: 280px; border-right: 1px solid var(--border); padding-right: 14px; overflow: auto; max-height: 75vh; }
.mlb-detail__preview { background: var(--surface-2); border-radius: 8px; padding: 8px; margin-bottom: 10px; text-align: center; }
.mlb-detail__preview img, .mlb-detail__preview video { max-width: 100%; max-height: 220px; }
.mlb-detail__preview audio { width: 100%; }
.mlb-meta { display: grid; grid-template-columns: auto 1fr; gap: 2px 10px; font-size: 12px; margin-bottom: 8px; }
.mlb-meta dt { color: var(--muted); font-weight: 400; }
.mlb-meta dd { margin: 0; text-align: left; word-break: break-all; }
.mlb-label { display: block; font-size: 12px; color: var(--muted); margin: 8px 0 4px; }
.mlb-url { display: flex; gap: 6px; }
.mlb-detail__actions { display: flex; justify-content: space-between; margin-top: 14px; }
.mlb-drop { position: absolute; inset: 0; z-index: 20; background: var(--overlay-bg); border: 2px dashed var(--accent); color: var(--accent-text); border-radius: 10px; display: flex; align-items: center; justify-content: center; text-align: center; font-size: 18px; font-weight: 600; pointer-events: none; }
.mlb-drop small { display: block; font-size: 12px; font-weight: 400; color: var(--muted); margin-top: 6px; }
.mlb-sentinel { grid-column: 1 / -1; height: 1px; }
.mlb-shimmer { background: linear-gradient(90deg, #eef0f3 25%, #f7f8fa 37%, #eef0f3 63%); background-size: 400% 100%; animation: mlb-sh 1.4s ease infinite; }
@keyframes mlb-sh { 0% { background-position: 100% 50%; } 100% { background-position: 0 50%; } }
.mlb-sk { cursor: default; pointer-events: none; }
.mlb-tree-toggle, .mlb-tree-backdrop { display: none; }
@media (max-width: 991px) {
  .mlb { grid-template-columns: 1fr; min-height: 0; }
  /* folders become an off-canvas drawer (RTL: from the right) */
  .mlb-tree-toggle { display: inline-block; }
  .mlb-tree { position: fixed; top: 0; right: 0; bottom: 0; z-index: 1050; width: 280px; max-width: 85vw; max-height: none; background: var(--surface, #fff); border: 0; padding: 14px; box-shadow: -4px 0 24px rgba(0,0,0,.18); transform: translateX(105%); transition: transform .2s ease; }
  .mlb-tree.open { transform: none; }
  .mlb-tree-backdrop { position: fixed; inset: 0; z-index: 1049; background: rgba(0,0,0,.35); }
  .mlb-tree-backdrop.open { display: block; }
  .mlb-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); max-height: none; }
  .mlb--picker .mlb-grid { max-height: none; }
  .mlb-detail { width: auto; border-right: 0; border-top: 1px solid var(--border); padding: 10px 0 0; }
}

/* square tiles: guard against global .card/img rules */
.mlb-grid > .mlb-card { width: auto; min-width: 0; height: auto; margin: 0; }
.mlb-card.active { border-color: var(--accent); }
/* lightbox */
.mlb-lb { position: fixed; inset: 0; z-index: 2000; background: rgba(0, 0, 0, .7); display: flex; align-items: center; justify-content: center; padding: 24px; direction: rtl; }
.mlb-lb__box { background: var(--surface, #fff); color: var(--text); width: min(1280px, 100%); height: min(860px, 100%); border-radius: 10px; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 10px 40px rgba(0,0,0,.35); }
.mlb-lb__head { display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-bottom: 1px solid var(--border); }
.mlb-lb__pos { font-size: 12px; color: var(--muted); }
.mlb-lb__icon { display: inline-flex; align-items: center; justify-content: center; padding: 0; width: 36px; height: 36px; border: 1px solid var(--border); background: transparent; color: inherit; border-radius: 6px; font-size: 18px; line-height: 1; cursor: pointer; }
.mlb-lb__icon:disabled { opacity: .35; cursor: default; }
.mlb-lb__body { flex: 1; min-height: 0; display: flex; flex-direction: row-reverse; }
.mlb-lb__media { flex: 1; min-width: 0; display: flex; align-items: center; justify-content: center; padding: 16px; background: repeating-conic-gradient(#f1f1f1 0% 25%, #fafafa 0% 50%) 50% / 20px 20px; }
.mlb-lb__media img, .mlb-lb__media video { max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; }
.mlb-lb__side { width: 340px; flex-shrink: 0; border-right: 1px solid var(--border); padding: 14px; overflow: auto; }
@media (max-width: 575px) {
  .mlb-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
  .mlb-input--search { width: 100%; }
}
@media (max-width: 860px) {
  .mlb-lb { padding: 0; }
  .mlb-lb__box { height: 100%; border-radius: 0; }
  .mlb-lb__body { flex-direction: column; overflow: auto; }
  .mlb-lb__media { min-height: 45vh; }
  .mlb-lb__side { width: auto; border-right: 0; border-top: 1px solid var(--border); }
}
</style>
